import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dashboard,
  People,
  AccessTime,
  Assessment,
  EventNote,
  Receipt,
  Business,
  CalendarToday,
  ReceiptLong,
  Send,
  BarChart,
  Description,
  Work,
  TrendingUp,
  Flag,
  History,
  Group,
  Security,
  Settings,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  Badge as EmployeeIcon,
  CheckBox as CheckBoxIcon,
  Search,
  RestartAlt,
  Save,
  People as PeopleIcon,
  ViewList,
  Close,
  SelectAll,
} from "@mui/icons-material";
import {
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../services/api";
import Avatar from "../components/ui/Avatar";
import { normalizeNavPermissions } from "../utils/navPermissions";

// ─── Master nav items catalogue (mirrors MainLayout menuItems) ─────────────
const ALL_NAV_ITEMS = [
  { key: "/dashboard", label: "Dashboard", icon: Dashboard, roles: ["ADMIN", "MANAGER"] },
  { key: "/employees", label: "Employees", icon: People, roles: ["ADMIN", "HR", "MANAGER"] },
  { key: "/attendance", label: "Attendance", icon: AccessTime, roles: ["ADMIN", "MANAGER"] },
  { key: "/attendance/report", label: "Attendance Report", icon: Assessment, roles: ["ADMIN", "MANAGER"] },
  { key: "/leave", label: "Leave", icon: EventNote, roles: ["ADMIN", "MANAGER"] },
  { key: "/expenses", label: "Expenses", icon: Receipt, roles: ["ADMIN", "MANAGER"] },
  { key: "/departments", label: "Departments", icon: Business, roles: ["ADMIN", "MANAGER"] },
  { key: "/holidays", label: "Holidays", icon: CalendarToday, roles: ["ADMIN", "MANAGER", "HR"] },
  { key: "/salary-slip", label: "Salary Slips", icon: ReceiptLong, roles: ["ADMIN", "MANAGER"] },
  { key: "/payroll/send-salary-slip", label: "Send Salary Slips", icon: Send, roles: ["ADMIN", "MANAGER"] },
  { key: "/payroll/status", label: "Payroll Status", icon: ReceiptLong, roles: ["ADMIN", "MANAGER"] },
  { key: "/goals", label: "Goals", icon: Flag, roles: ["ADMIN", "MANAGER", "HR", "EMPLOYEE"] },
  { key: "/activity", label: "My Activity", icon: History, roles: ["ADMIN", "MANAGER", "HR", "EMPLOYEE"] },
  { key: "/activity/team", label: "Team Activity", icon: Group, roles: ["ADMIN", "MANAGER"] },
  { key: "/performance", label: "Performance", icon: TrendingUp, roles: ["ADMIN", "MANAGER"] },
  { key: "/documents", label: "Document Vault", icon: Description, roles: ["ADMIN", "MANAGER"] },
  { key: "/recruitment", label: "Recruitment", icon: Work, roles: ["ADMIN", "MANAGER"] },
  { key: "/dashboard/analytics", label: "HR Analytics", icon: BarChart, roles: ["ADMIN", "MANAGER"] },
  { key: "/role-management", label: "Role Management", icon: Security, roles: ["ADMIN"] },
  { key: "/nav-permissions", label: "Nav Permissions", icon: Settings, roles: ["ADMIN"] },
];

function getRoleItems(role) {
  const r = (role || "EMPLOYEE").toUpperCase();
  return ALL_NAV_ITEMS.filter((item) => item.roles.includes(r));
}

function getRoleIcon(role) {
  switch ((role || "").toUpperCase()) {
    case "ADMIN": return <AdminIcon className="text-purple-500" fontSize="small" />;
    case "MANAGER": return <ManagerIcon className="text-blue-500" fontSize="small" />;
    case "HR": return <EmployeeIcon className="text-orange-500" fontSize="small" />;
    default: return <EmployeeIcon className="text-green-500" fontSize="small" />;
  }
}

function getRoleBadgeClass(role) {
  switch ((role || "").toUpperCase()) {
    case "ADMIN": return "bg-purple-100 text-purple-700";
    case "MANAGER": return "bg-blue-100 text-blue-700";
    case "HR": return "bg-orange-100 text-orange-700";
    default: return "bg-green-100 text-green-700";
  }
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.04 } },
};
const rowVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function NavPermissionsManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");

  // Single user edit dialog — { path, access }[]
  const [editUser, setEditUser] = useState(null);
  const [editEntries, setEditEntries] = useState([]);

  // Bulk assign dialog — { path, access }[]
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTargetRole, setBulkTargetRole] = useState("EMPLOYEE");
  const [bulkEntries, setBulkEntries] = useState([]);
  const [bulkApplyTo, setBulkApplyTo] = useState("all"); // "all" | "role"
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/users/nav-permissions");
      const data = Array.isArray(res.data) ? res.data
        : Array.isArray(res.data?.data) ? res.data.data : [];
      setUsers(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchesSearch =
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase());
      const matchesRole =
        roleFilter === "ALL" || (u.role || "").toUpperCase() === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [users, search, roleFilter]);

  // ── Single user edit ──────────────────────────────────────────────────────
  const openEdit = (user) => {
    const roleDefault = getRoleItems(user.role).map((i) => i.key);
    if (user.navPermissions != null) {
      const norm = normalizeNavPermissions(user.navPermissions);
      setEditEntries(Array.isArray(norm) ? norm : []);
    } else {
      setEditEntries(roleDefault.map((path) => ({ path, access: "write" })));
    }
    setEditUser(user);
  };

  const getEntry = (entries, key) => entries.find((e) => e.path === key);

  const toggleEditItem = (key) => {
    setEditEntries((prev) => {
      const i = prev.findIndex((e) => e.path === key);
      if (i === -1) return [...prev, { path: key, access: "write" }];
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const setEditHolidayAccess = (access) => {
    const key = "/holidays";
    setEditEntries((prev) => {
      const i = prev.findIndex((e) => e.path === key);
      if (i === -1) return [...prev, { path: key, access }];
      const next = [...prev];
      next[i] = { path: key, access };
      return next;
    });
  };

  const setEditEmployeesAccess = (access) => {
    const key = "/employees";
    setEditEntries((prev) => {
      const i = prev.findIndex((e) => e.path === key);
      if (i === -1) return [...prev, { path: key, access }];
      const next = [...prev];
      next[i] = { path: key, access };
      return next;
    });
  };

  const selectAllEdit = () => {
    setEditEntries(ALL_NAV_ITEMS.map((i) => ({ path: i.key, access: "write" })));
  };

  const clearAllEdit = () => setEditEntries([]);

  const saveEditUser = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const uid = editUser.id || editUser._id;
      const res = await api.put(`/users/${uid}/nav-permissions`, {
        navPermissions: editEntries,
      });
      const updated = res.data?.data ?? res.data;
      setUsers((prev) =>
        prev.map((u) =>
          String(u.id || u._id) === String(uid)
            ? { ...u, navPermissions: updated.navPermissions }
            : u
        )
      );
      toast.success(`Nav permissions updated for ${editUser.name}`);
      setEditUser(null);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async (user) => {
    setSaving(true);
    try {
      const uid = user.id || user._id;
      await api.put(`/users/${uid}/nav-permissions`, { navPermissions: null });
      setUsers((prev) =>
        prev.map((u) =>
          String(u.id || u._id) === String(uid)
            ? { ...u, navPermissions: null }
            : u
        )
      );
      toast.success(`Reset to role defaults for ${user.name}`);
      setEditUser(null);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  // ── Bulk assign ───────────────────────────────────────────────────────────
  const openBulk = () => {
    setBulkEntries(getRoleItems("EMPLOYEE").map((i) => ({ path: i.key, access: "write" })));
    setBulkTargetRole("EMPLOYEE");
    setBulkApplyTo("role");
    setBulkOpen(true);
  };

  const toggleBulkItem = (key) => {
    setBulkEntries((prev) => {
      const i = prev.findIndex((e) => e.path === key);
      if (i === -1) return [...prev, { path: key, access: "write" }];
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const setBulkHolidayAccess = (access) => {
    const key = "/holidays";
    setBulkEntries((prev) => {
      const i = prev.findIndex((e) => e.path === key);
      if (i === -1) return [...prev, { path: key, access }];
      const next = [...prev];
      next[i] = { path: key, access };
      return next;
    });
  };

  const setBulkEmployeesAccess = (access) => {
    const key = "/employees";
    setBulkEntries((prev) => {
      const i = prev.findIndex((e) => e.path === key);
      if (i === -1) return [...prev, { path: key, access }];
      const next = [...prev];
      next[i] = { path: key, access };
      return next;
    });
  };

  const saveBulk = async () => {
    setBulkSaving(true);
    try {
      let userIds;
      if (bulkApplyTo === "all") {
        userIds = "all";
      } else {
        userIds = users
          .filter((u) => (u.role || "").toUpperCase() === bulkTargetRole)
          .map((u) => String(u.id || u._id));
        if (userIds.length === 0) {
          toast.warn("No users found for that role");
          return;
        }
      }

      await api.put("/users/nav-permissions/bulk", {
        userIds,
        navPermissions: bulkEntries,
      });

      toast.success(
        `Nav permissions applied to ${bulkApplyTo === "all" ? "all employees" : `all ${bulkTargetRole}s`}`
      );
      setBulkOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };

  // ── Nav items visible count helper ───────────────────────────────────────
  const getVisibleCount = (user) => {
    if (user.navPermissions != null) {
      const n = normalizeNavPermissions(user.navPermissions);
      return {
        count: Array.isArray(n) ? n.length : 0,
        total: ALL_NAV_ITEMS.length,
        isDefault: false,
        isLimited: false,
      };
    }
    const roleCount = getRoleItems(user.role).length;
    return { count: roleCount, total: ALL_NAV_ITEMS.length, isDefault: true, isLimited: false };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="h-10 w-64 bg-gray-200 rounded-xl animate-pulse mb-6" />
          <LinearProgress className="rounded" />
          <div className="mt-6 space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 bg-white rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-7xl mx-auto space-y-6"
      >
        {/* ── Header ── */}
        <motion.div variants={rowVariants} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <ViewList className="text-primary" />
              Nav Permissions
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              Control which sidebar modules each user can access. Changes take effect on their next page load.
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={openBulk}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-2xl font-semibold text-sm shadow-soft hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            <PeopleIcon fontSize="small" />
            Bulk Assign
          </motion.button>
        </motion.div>

        {/* ── Stats ── */}
        <motion.div variants={rowVariants} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Users", value: users.length, color: "text-gray-700", bg: "bg-gray-100" },
            { label: "Custom Config", value: users.filter((u) => u.navPermissions != null).length, color: "text-primary", bg: "bg-primary/10" },
            { label: "Role Defaults", value: users.filter((u) => u.navPermissions == null).length, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "Employees", value: users.filter((u) => (u.role || "").toUpperCase() === "EMPLOYEE").length, color: "text-blue-600", bg: "bg-blue-50" },
          ].map((s) => (
            <div key={s.label} className={`${s.bg} rounded-2xl p-4`}>
              <p className="text-xs font-medium text-gray-500">{s.label}</p>
              <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </motion.div>

        {/* ── Filters ── */}
        <motion.div variants={rowVariants} className="bg-white rounded-2xl shadow-soft p-4 flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2 flex-1 bg-gray-50 rounded-xl px-3 py-2">
            <Search className="text-gray-400 w-4 h-4" />
            <input
              className="bg-transparent outline-none text-sm text-gray-700 w-full placeholder:text-gray-400"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["ALL", "ADMIN", "MANAGER", "HR", "EMPLOYEE"].map((r) => (
              <button
                key={r}
                onClick={() => setRoleFilter(r)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                  roleFilter === r
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {r === "ALL" ? "All Roles" : r.charAt(0) + r.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </motion.div>

        {/* ── Users Table ── */}
        <motion.div variants={rowVariants} className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {filteredUsers.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <PeopleIcon className="text-gray-200 mb-3" style={{ fontSize: 48 }} />
              <p className="font-medium">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Visible Nav Items</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Config</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  <AnimatePresence>
                    {filteredUsers.map((user) => {
                      const { count, total, isDefault, isLimited } = getVisibleCount(user);
                      const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                      const uid = user.id || user._id;

                      return (
                        <motion.tr
                          key={uid}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <Avatar name={user.name} size="sm" />
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{user.name}</p>
                                <p className="text-xs text-gray-400">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-semibold ${getRoleBadgeClass(user.role)}`}>
                              {getRoleIcon(user.role)}
                              {user.role}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3 min-w-[140px]">
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-gray-600 font-medium">{count}/{total}</span>
                                  <span className="text-xs text-gray-400">{percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                  <div
                                    className="bg-primary h-1.5 rounded-full transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            {!isDefault ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-lg text-xs font-medium">
                                Custom
                              </span>
                            ) : isLimited ? (
                              <Tooltip title="Employee Limited Release flag is ON — only Goals + My Activity are shown. Set custom permissions to unlock more.">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 rounded-lg text-xs font-medium cursor-help">
                                  Limited Mode
                                </span>
                              </Tooltip>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-600 rounded-lg text-xs font-medium">
                                Role Default
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Tooltip title="Reset to role defaults">
                                <button
                                  onClick={() => resetToDefaults(user)}
                                  disabled={isDefault || saving}
                                  className="p-1.5 rounded-xl text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <RestartAlt fontSize="small" />
                                </button>
                              </Tooltip>
                              <motion.button
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                onClick={() => openEdit(user)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-xl text-xs font-semibold hover:opacity-90 transition-opacity"
                              >
                                Configure
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* ── Single User Edit Dialog ── */}
      <Dialog
        open={!!editUser}
        onClose={() => setEditUser(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 20 } }}
      >
        {editUser && (
          <>
            <DialogTitle className="border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar name={editUser.name} size="sm" />
                  <div>
                    <p className="text-base font-bold text-gray-800">{editUser.name}</p>
                    <p className="text-xs text-gray-400">{editUser.email}</p>
                  </div>
                </div>
                <button onClick={() => setEditUser(null)} className="p-1.5 rounded-xl hover:bg-gray-100 transition-colors">
                  <Close fontSize="small" className="text-gray-400" />
                </button>
              </div>
            </DialogTitle>

            <DialogContent className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700">
                  Choose modules for {editUser.name}
                </p>
                <div className="flex gap-2">
                  <button onClick={selectAllEdit} className="flex items-center gap-1 text-xs text-primary hover:underline">
                    <SelectAll fontSize="small" /> Select all
                  </button>
                  <button onClick={clearAllEdit} className="flex items-center gap-1 text-xs text-red-400 hover:underline">
                    <Close fontSize="small" /> None
                  </button>
                </div>
              </div>

              <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
                {ALL_NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const entry = getEntry(editEntries, item.key);
                  const checked = !!entry;
                  const inRoleDefault = getRoleItems(editUser.role).some((r) => r.key === item.key);
                  return (
                    <div
                      key={item.key}
                      className={`flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-xl transition-colors ${
                        checked ? "bg-primary/8 border border-primary/20" : "hover:bg-gray-50 border border-transparent"
                      }`}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggleEditItem(item.key)}
                        className="p-0"
                      />
                      <Icon fontSize="small" className={checked ? "text-primary" : "text-gray-400"} />
                      <span className={`text-sm font-medium flex-1 min-w-0 ${checked ? "text-gray-800" : "text-gray-500"}`}>
                        {item.label}
                      </span>
                      {checked && item.key === "/holidays" && (
                        <div className="ml-auto inline-flex rounded-lg border border-gray-200 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setEditHolidayAccess("read")}
                            className={`px-2 py-1 text-xs font-semibold ${entry?.access === "read" ? "bg-gray-800 text-white" : "bg-white text-gray-600"}`}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditHolidayAccess("write")}
                            className={`px-2 py-1 text-xs font-semibold ${entry?.access === "write" ? "bg-primary text-white" : "bg-white text-gray-600"}`}
                          >
                            Manage
                          </button>
                        </div>
                      )}
                      {checked && item.key === "/employees" && (
                        <div className="ml-auto inline-flex rounded-lg border border-gray-200 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setEditEmployeesAccess("read")}
                            className={`px-2 py-1 text-xs font-semibold ${entry?.access === "read" ? "bg-gray-800 text-white" : "bg-white text-gray-600"}`}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditEmployeesAccess("write")}
                            className={`px-2 py-1 text-xs font-semibold ${entry?.access === "write" ? "bg-primary text-white" : "bg-white text-gray-600"}`}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                      {!inRoleDefault && (
                        <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded-md font-medium">
                          elevated
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-xl px-3 py-2">
                <CheckBoxIcon fontSize="small" className="text-primary" />
                {editEntries.length} of {ALL_NAV_ITEMS.length} modules selected
              </div>
            </DialogContent>

            <DialogActions className="px-5 pb-4 gap-2">
              <Button onClick={() => resetToDefaults(editUser)} disabled={saving} color="warning" variant="outlined" size="small" startIcon={<RestartAlt />}>
                Reset to Defaults
              </Button>
              <div className="flex-1" />
              <Button onClick={() => setEditUser(null)} size="small">Cancel</Button>
              <Button
                onClick={saveEditUser}
                disabled={saving}
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                className="bg-primary"
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Bulk Assign Dialog ── */}
      <Dialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 20 } }}
      >
        <DialogTitle className="border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base font-bold text-gray-800">Bulk Assign Nav Items</p>
              <p className="text-xs text-gray-400 mt-0.5">Apply the same nav config to multiple employees at once</p>
            </div>
            <button onClick={() => setBulkOpen(false)} className="p-1.5 rounded-xl hover:bg-gray-100 transition-colors">
              <Close fontSize="small" className="text-gray-400" />
            </button>
          </div>
        </DialogTitle>

        <DialogContent className="pt-4 space-y-4">
          {/* Apply to */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Apply to</p>
            <div className="flex gap-2 flex-wrap">
              {[
                { id: "role", label: "Specific Role" },
                { id: "all", label: "All Employees (Everyone)" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setBulkApplyTo(opt.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${
                    bulkApplyTo === opt.id
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-gray-600 border-gray-200 hover:border-primary/40"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Role selector */}
          {bulkApplyTo === "role" && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Role</p>
              <div className="flex gap-2">
                {["EMPLOYEE", "HR", "MANAGER", "ADMIN"].map((r) => (
                    <button
                      key={r}
                      onClick={() => {
                        setBulkTargetRole(r);
                        setBulkEntries(getRoleItems(r).map((i) => ({ path: i.key, access: "write" })));
                      }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${
                      bulkTargetRole === r
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-600 border-gray-200 hover:border-primary/40"
                    }`}
                  >
                    {r.charAt(0) + r.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Nav item selector */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Nav Items</p>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setBulkEntries(ALL_NAV_ITEMS.map((i) => ({ path: i.key, access: "write" })))
                  }
                  className="text-xs text-primary hover:underline"
                >
                  Select all
                </button>
                <button onClick={() => setBulkEntries([])} className="text-xs text-red-400 hover:underline">
                  Clear
                </button>
              </div>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {ALL_NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const entry = getEntry(bulkEntries, item.key);
                const checked = !!entry;
                const roleForCheck = bulkApplyTo === "all" ? "EMPLOYEE" : bulkTargetRole;
                const inRoleDefault = getRoleItems(roleForCheck).some((r) => r.key === item.key);
                return (
                  <div
                    key={item.key}
                    className={`flex flex-wrap items-center gap-2 px-3 py-2 rounded-xl transition-colors ${
                      checked ? "bg-primary/8 border border-primary/20" : "hover:bg-gray-50 border border-transparent"
                    }`}
                  >
                    <Checkbox size="small" checked={checked} onChange={() => toggleBulkItem(item.key)} className="p-0" />
                    <Icon fontSize="small" className={checked ? "text-primary" : "text-gray-400"} />
                    <span className={`text-sm flex-1 min-w-0 ${checked ? "text-gray-800 font-medium" : "text-gray-500"}`}>
                      {item.label}
                    </span>
                    {checked && item.key === "/holidays" && (
                      <div className="ml-auto inline-flex rounded-lg border border-gray-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setBulkHolidayAccess("read")}
                          className={`px-2 py-1 text-xs font-semibold ${entry?.access === "read" ? "bg-gray-800 text-white" : "bg-white text-gray-600"}`}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => setBulkHolidayAccess("write")}
                          className={`px-2 py-1 text-xs font-semibold ${entry?.access === "write" ? "bg-primary text-white" : "bg-white text-gray-600"}`}
                        >
                          Manage
                        </button>
                      </div>
                    )}
                    {checked && item.key === "/employees" && (
                      <div className="ml-auto inline-flex rounded-lg border border-gray-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setBulkEmployeesAccess("read")}
                          className={`px-2 py-1 text-xs font-semibold ${entry?.access === "read" ? "bg-gray-800 text-white" : "bg-white text-gray-600"}`}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => setBulkEmployeesAccess("write")}
                          className={`px-2 py-1 text-xs font-semibold ${entry?.access === "write" ? "bg-primary text-white" : "bg-white text-gray-600"}`}
                        >
                          Edit
                        </button>
                      </div>
                    )}
                    {!inRoleDefault && (
                      <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded-md font-medium">
                        elevated
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-xl px-3 py-2">
              <CheckBoxIcon fontSize="small" className="text-primary" />
              {bulkEntries.length} of {ALL_NAV_ITEMS.length} items selected
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
            This will overwrite existing custom configurations for the selected group. Users with role defaults will also be updated.
          </div>
        </DialogContent>

        <DialogActions className="px-5 pb-4 gap-2">
          <Button onClick={() => setBulkOpen(false)} size="small">Cancel</Button>
          <Button
            onClick={saveBulk}
            disabled={bulkSaving || bulkEntries.length === 0}
            variant="contained"
            size="small"
            startIcon={bulkSaving ? <CircularProgress size={14} color="inherit" /> : <Save />}
            className="bg-primary"
          >
            Apply to {bulkApplyTo === "all" ? "Everyone" : `All ${bulkTargetRole.charAt(0) + bulkTargetRole.slice(1).toLowerCase()}s`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
